/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { Skeleton } from '@mui/material';
import { ConfirmationDelete, ConfirmationDialog } from 'components/Dialogs';
import { compareDataAndNoteIds } from 'components/SpreadsheetNotes/utilities/compareDataAndNoteIds';
import { DialogContext } from 'context';
import { NotesContext } from 'context/NotesContext';
import { useGetNotes } from 'services/hooks/notes/';
import NoteList from './NoteList';
import NotesNotAvailable from './NotesNotAvailable';
import { NoteData, NotesProps } from './types';

// @ts-ignore
const WrapperComponent = props => <ConfirmationDialog {...props} title="Delete" />;
const ContentComponent = () => <ConfirmationDelete itemName="note" />;

const Notes = ({
  pageType,
  pageTypeKey,
  pageTypeId,
  isApproach,
  notes,
  setNotes,
  onAddNote,
  onUpdateNotes,
  onDeleteNote,
  isLoading,
  appBarSx,
}: NotesProps) => {
  const { showDialog } = useContext(DialogContext);
  const { data } = useGetNotes({ pageType, pageTypeKey, pageTypeId });

  useEffect(() => {
    if (!compareDataAndNoteIds(notes ?? [], data ?? [])) {
      setNotes(data ?? []);
    }
  }, [setNotes, data, notes]);

  const onDeleteNoteConfirmationDialog = useCallback(
    (updatedNote: NoteData) => {
      // @ts-ignore
      showDialog({
        wrapper: WrapperComponent,
        content: ContentComponent,
        actions: [
          {
            label: 'Cancel',
            type: 'cancel',
          },
          {
            label: 'Delete',
            variant: 'contained',
            type: 'danger',
            callback: () => onDeleteNote?.(updatedNote),
          },
        ],
      });
    },
    [onDeleteNote, showDialog]
  );

  const onDeleteNewNote = useCallback(
    (note: NoteData) => {
      if (note.isNew && note.content.length > 0) {
        onDeleteNoteConfirmationDialog(note);
      } else {
        onDeleteNote?.(note);
      }
    },
    [onDeleteNote, onDeleteNoteConfirmationDialog]
  );

  const notesContextProviderValue = useMemo(
    () => ({
      pageType,
      pageTypeKey,
      pageTypeId,
      ...(onAddNote && { onAddNote }),
      ...(onUpdateNotes && { onUpdateNotes }),
      ...(onDeleteNote && { onDeleteNote: onDeleteNoteConfirmationDialog }),
      onDeleteNewNote,
    }),
    [
      pageType,
      pageTypeKey,
      pageTypeId,
      onAddNote,
      onUpdateNotes,
      onDeleteNote,
      onDeleteNoteConfirmationDialog,
      onDeleteNewNote,
    ]
  );

  if ((!isApproach && !pageTypeId) || isLoading) {
    return <Skeleton height="5rem" />;
  }

  return (
    <>
      {pageTypeId ? (
        <NotesContext.Provider value={notesContextProviderValue}>
          <NoteList
            pageTypeKey={pageTypeKey}
            pageType={pageType}
            pageTypeId={pageTypeId}
            notes={notes}
            appBarSx={appBarSx}
          />
        </NotesContext.Provider>
      ) : (
        <NotesNotAvailable />
      )}
    </>
  );
};

export default Notes;
